import styled from 'styled-components';
import {QUOTE_DOC_COLORS as C} from '../pdf/quotePdfStyles';
import type {QuoteFont} from '../resolver/types';

// CSS stacks approximating the PDF's built-in faces, so the live preview reads like the
// document the download produces.
export const previewFontStack = (font: QuoteFont): string => {
	if (font === 'times') return "'Times New Roman', Times, serif";
	if (font === 'courier') return "'Courier New', Courier, monospace";
	return 'Helvetica, Arial, sans-serif';
};

// One A4 sheet at the PDF's native point size (595×842), so every measurement below can be
// copied 1:1 from the PDF stylesheet. The pane scrolls; each sheet keeps its own chrome.
export const PageSheet = styled.section<{$font: QuoteFont}>`
	position: relative;
	width: 595px;
	min-height: 842px;
	margin: 0 auto 24px;
	padding: 84px 48px 72px;
	background: #ffffff;
	box-shadow: 0 1px 3px rgba(29, 32, 70, 0.14), 0 6px 18px rgba(29, 32, 70, 0.08);
	font-family: ${p => previewFontStack(p.$font)};
	font-size: 10px;
	color: ${C.ink};
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
`;

export const Band = styled.div<{$cover?: boolean}>`
	position: absolute;
	top: 28px;
	left: 48px;
	right: 48px;
	display: flex;
	justify-content: ${p => (p.$cover ? 'flex-end' : 'space-between')};
	align-items: center;
	padding-bottom: 10px;
	border-bottom: ${p => (p.$cover ? 'none' : `1px solid ${C.borderMid}`)};
`;

export const BandRun = styled.div`
	display: flex;
	align-items: center;
`;

export const BandLogoImage = styled.img`
	height: 14px;
	max-width: 42px;
	object-fit: contain;
	object-position: left;
	margin-right: 8px;
`;

export const BandLogoWord = styled.span`
	font-size: 9px;
	font-weight: 700;
	color: ${C.ink70};
	margin-right: 8px;
`;

export const BandRunText = styled.span`
	font-size: 8px;
	color: ${C.ink55};
`;

export const BandConfidential = styled.span`
	font-size: 7px;
	letter-spacing: 1.1px;
	text-transform: uppercase;
	color: ${C.ink40};
`;

export const PageContent = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
`;

export const Foot = styled.div`
	position: absolute;
	bottom: 26px;
	left: 48px;
	right: 48px;
	display: flex;
	justify-content: space-between;
	border-top: 1px solid ${C.borderSoft};
	padding-top: 8px;
	font-size: 8px;
	color: ${C.ink45};
`;

export const Section = styled.div`
	margin-bottom: 22px;
`;

export const SectionLabelRow = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 13px;
`;

export const SectionLabelBar = styled.span<{$brand: string}>`
	width: 16px;
	height: 2px;
	border-radius: 1px;
	margin-right: 8px;
	background: ${p => p.$brand};
`;

export const SectionLabelText = styled.span<{$brand: string}>`
	font-size: 9px;
	font-weight: 700;
	letter-spacing: 1.3px;
	text-transform: uppercase;
	color: ${p => p.$brand};
`;

export const Paragraph = styled.p`
	font-size: 10px;
	line-height: 1.65;
	margin: 0 0 9px;
	color: ${C.ink86};
`;

// ---- Cover -------------------------------------------------------------------
export const CoverContent = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	padding: 6px 0;
`;

export const CoverLogoImage = styled.img`
	height: 40px;
	max-width: 150px;
	object-fit: contain;
	object-position: left;
`;

export const CoverLogoWord = styled.div`
	font-size: 15px;
	font-weight: 700;
`;

export const CoverEyebrow = styled.div<{$brand: string}>`
	font-size: 9px;
	font-weight: 700;
	letter-spacing: 1.5px;
	text-transform: uppercase;
	margin-bottom: 13px;
	color: ${p => p.$brand};
`;

export const CoverTitle = styled.h1`
	/* The app styles headings globally; a matching h1 rule outranks the font-family inherited from
	   PageSheet, so without this the document's most prominent line ignores the font picker while
	   the body text follows it. */
	font-family: inherit;
	font-size: 32px;
	font-weight: 700;
	line-height: 1.1;
	margin: 0 0 10px;
`;

export const CoverFor = styled.div`
	font-size: 16px;
	color: ${C.ink60};
	margin-bottom: 19px;
`;

export const CoverPrepared = styled.div`
	font-size: 10px;
	color: ${C.ink70};
`;

export const CoverMeta = styled.div`
	display: flex;
	flex-wrap: wrap;
	border-top: 1px solid ${C.borderMeta};
	padding-top: 19px;
`;

export const CoverMetaCell = styled.div`
	width: 50%;
	margin-bottom: 14px;
`;

export const CoverMetaLabel = styled.div`
	font-size: 8px;
	letter-spacing: 0.8px;
	text-transform: uppercase;
	color: ${C.ink45};
	margin-bottom: 4px;
`;

export const CoverMetaValue = styled.div<{$brand?: string}>`
	font-size: 12px;
	font-weight: 700;
	color: ${p => p.$brand || 'inherit'};
`;

// ---- Estimate table ----------------------------------------------------------
export const TableHeadRow = styled.div`
	display: flex;
	border-bottom: 1.5px solid ${C.borderStrong};
	padding-bottom: 7px;
`;

export const Th = styled.span<{$num?: boolean}>`
	font-size: 8px;
	font-weight: 700;
	letter-spacing: 0.8px;
	text-transform: uppercase;
	color: ${C.ink50};
	text-align: ${p => (p.$num ? 'right' : 'left')};
`;

export const ColLabel = styled.span`
	flex: 1;
	padding-right: 8px;
	min-width: 0;
`;

export const ColNum = styled.span`
	width: 74px;
	flex: none;
	text-align: right;
`;

export const PhaseRow = styled.div`
	display: flex;
	align-items: flex-end;
	padding: 12px 0 6px;
	border-bottom: 1px solid ${C.borderMid};
`;

export const PhaseIndex = styled.span<{$brand: string}>`
	font-size: 7.5px;
	font-weight: 700;
	letter-spacing: 0.8px;
	text-transform: uppercase;
	margin-right: 8px;
	color: ${p => p.$brand};
`;

export const PhaseName = styled.span`
	font-size: 11px;
	font-weight: 700;
`;

export const PhaseSubtotal = styled.span`
	font-size: 10.5px;
	font-weight: 700;
`;

export const DeliverableRow = styled.div`
	display: flex;
	align-items: center;
	padding: 11px 0 2px 10px;
`;

export const DeliverableLabel = styled.span<{$brand: string}>`
	font-size: 7px;
	font-weight: 700;
	letter-spacing: 0.9px;
	text-transform: uppercase;
	margin-right: 8px;
	color: ${p => p.$brand};
`;

export const DeliverableName = styled.span`
	font-size: 8.5px;
	font-weight: 700;
`;

export const ItemRow = styled.div`
	display: flex;
	padding: 8px 0;
	border-bottom: 1px solid ${C.borderFaint};
`;

export const ItemName = styled.span`
	font-size: 10px;
`;

export const ItemNum = styled.span`
	font-size: 10px;
`;

export const ItemTotal = styled.span`
	font-size: 10px;
	font-weight: 700;
`;

export const ExpenseTag = styled.span`
	font-size: 7px;
	font-weight: 700;
	letter-spacing: 0.9px;
	text-transform: uppercase;
	color: ${C.ink42};
	margin-right: 7px;
`;

export const ExpensesTotalLabel = styled.span`
	font-size: 10px;
	font-weight: 700;
`;

export const RollupRow = styled.div`
	display: flex;
	padding: 10px 0;
	border-bottom: 1px solid ${C.borderFaint};
`;

export const RollupName = styled.span`
	font-size: 10.5px;
	font-weight: 700;
`;

export const TotalsWrap = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: 18px;
`;

export const TotalsGrid = styled.div`
	width: 250px;
`;

export const TotalsRow = styled.div`
	display: flex;
	justify-content: space-between;
	margin-bottom: 7px;
	font-size: 10px;
`;

export const TotalsLabel = styled.span`
	color: ${C.ink70};
`;

export const TotalsGrandRow = styled.div`
	display: flex;
	justify-content: space-between;
	border-top: 1.5px solid ${C.borderGrand};
	padding-top: 9px;
	margin-top: 3px;
	font-size: 12px;
	font-weight: 700;
`;

export const TotalsGrandValue = styled.span<{$brand: string}>`
	color: ${p => p.$brand};
`;

// ---- Terms -------------------------------------------------------------------
export const TermRow = styled.div`
	display: flex;
	margin-bottom: 13px;
`;

export const TermNumber = styled.span<{$brand: string}>`
	width: 16px;
	flex: none;
	font-size: 11px;
	font-weight: 700;
	color: ${p => p.$brand};
`;

export const TermBody = styled.div`
	flex: 1;
	min-width: 0;
`;

export const TermHeading = styled.div`
	font-size: 10px;
	font-weight: 700;
	margin-bottom: 3px;
`;

export const TermText = styled.p`
	font-size: 9.5px;
	line-height: 1.6;
	margin: 0;
	color: ${C.ink78};
`;

// ---- Agreement / signatures ---------------------------------------------------
export const SignGrid = styled.div`
	display: flex;
	margin-top: 28px;
`;

export const SignParty = styled.div<{$first?: boolean}>`
	flex: 1;
	margin-right: ${p => (p.$first ? '36px' : '0')};
`;

export const SignOrg = styled.div`
	font-size: 11px;
	font-weight: 700;
	margin-bottom: 18px;
`;

export const SignField = styled.div`
	margin-top: 22px;
`;

export const SignSignatureSpace = styled.div`
	height: 22px;
`;

export const SignLine = styled.div`
	border-bottom: 1px solid ${C.ink40};
	margin-bottom: 5px;
`;

export const SignLabel = styled.div`
	font-size: 8px;
	letter-spacing: 0.6px;
	text-transform: uppercase;
	color: ${C.ink50};
`;

export const SignNote = styled.p`
	margin: 28px 0 0;
	font-size: 8.5px;
	line-height: 1.5;
	color: ${C.ink50};
`;
